<template lang="pug">
.notification-table-component
  // テーブル
  b-table(
    :items="notificationItems"
    :fields="notificationFields"
    :current-page="currentPage"
    :per-page="perPage"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません

    // セル：タイトル
    template(#cell(title)="row")
      span.detail-link.link-text(@click="gotoNotificationDetail(row.item)") {{ row.item.title }}
      span.new(v-if="row.item.required") 未読
  
  .table-options.d-flex.flex-row.justify-content-end.align-items-center
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    // ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { notificationStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import NotificationsService, { NotificationsGetModel } from "@/commons/services/Notifications";

/**
 * お知らせテーブルコンポーネント
 */
@Component({})
export default class NotificationTable extends Vue {
  /** お知らせサービス */
  @Inject("NotificationsService") notificationsService!: NotificationsService;

  /** お知らせ列名 */
  notificationFields: Array<NotificationTableField> = [];
  /** お知らせ各行の情報 */
  notificationItems: Array<NotificationTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.notification.notificationTable.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.notification.notificationTable.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.notification.notificationTable.pageRange;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `notification-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const notificationFields: Array<NotificationTableField> = this.createNotificationFields();
    this.notificationFields = notificationFields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.notification.notificationTable.perPage);

    // 動的に変更する画面情報を作成する
    this.notificationItems = [];
    this.currentRows = this.notificationItems.length;

    await this.createView();

    return;
  }

  /**
   * ### お知らせの列名を作成する
   */
  public createNotificationFields(): Array<NotificationTableField> {
    let fields: Array<NotificationTableField> = [];

    fields.push({ key: "date", label: "日付", sortable: false, sortDirection: "desc", class: "date text-center" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["notification-list-per-page"]);

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * 画面情報を作成する
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // 写真テーブルに渡す各プロパティを作成する
    const notifications: Array<NotificationsGetModel> = await this.getNotifications();
    const notificationItems: Array<NotificationTableItem> = this.createNotificationItems(notifications);
    this.notificationItems = this.notificationItems.concat(notificationItems);
    this.currentRows = this.notificationItems.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.notificationsService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }
    return;
  }

  /**
   * ### お知らせを作成する
   */
  public async getNotifications(): Promise<Array<NotificationsGetModel>> {
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params = {
      startIndex: startIndex,
      endIndex: endIndex,
      order: "desc"
    };

    const response: Array<NotificationsGetModel> = await this.notificationsService.get(userStore, params);

    return response;
  }

  /**
   * ### お知らせの行を作成する
   */
  public createNotificationItems(notifications: Array<NotificationsGetModel>): Array<NotificationTableItem> {
    let items: Array<NotificationTableItem> = [];

    for (const notification of notifications) {
      items.push({
        id: notification.id,
        text: notification.text,
        deleteFlag: notification.deleteFlag,
        startPublish: notification.startPublish,
        endPublish: notification.endPublish,
        created: notification.created,
        updated: notification.updated,
        date: this.getDate(notification.startPublish),
        title: notification.title,
        required: this.isUnread(notification)
      });
    }

    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.notificationItems = [];
    this.currentRows = this.notificationItems.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### お知らせ詳細画面へ遷移する
   */
  public gotoNotificationDetail(notification: NotificationTableItem): void {
    // 個別にお知らせを取得するAPIは無いので、本画面表示の際に取得した情報をstoreに格納して受け渡す
    notificationStore.setNotification({ notification });
    this.$router.push(PcUrl.NotificationDetail);

    return;
  }

  /**
   * ### 日付(yyyy年mm月dd日)を返す
   */
  public getDate(date: number): string {
    return this.$dateUtil.convertNumberToString(date, "yyyy年mm月dd日");
  }

  /**
   * ### 未読かどうかを返す
   */
  public isUnread(notification: NotificationsGetModel): boolean {
    // 確認必須か
    if (!notification.required) {
      return false;
    }

    // ログイン時に取得した既読お知らせリストと照合し、未読か照合する
    if (0 <= userStore.loginUser.confirmedNotificationIds.indexOf(notification.id)) {
      return false;
    }

    return true;
  }
}

/** テーブルの列名の情報 */
export interface NotificationTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface NotificationTableItem {
  id: number;
  text: string;
  title: string;
  deleteFlag: boolean;
  startPublish: number;
  endPublish: number;
  required: boolean;
  created: number;
  updated: number;
  date: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.notification-table-component {
  .title {
    .detail-link {
      margin-right: 8px;
      word-break: break-all;
    }
    .new {
      min-width: 36px;
      @include validation-label();
    }
  }
}
</style>
